/**
 * Where each floor pixel's reflection comes from. A glossy floor mirrors
 * whatever stands on it (walls, doors, cars): for an upright surface the
 * reflection is the photo flipped about the line where that surface meets the
 * floor, column by column. Returns, per pixel, that contact row normalized to
 * 0..1 (the first floor row below the nearest object above), or -1 when the
 * pixel is not floor or nothing stands above it.
 *
 * Gaps in the floor shorter than `minGap` of the height (stains, mask noise)
 * are not objects and do not start a new reflection. The contact line is then
 * averaged along each row with neighbors on the same object, so a ragged mask
 * edge does not turn a straight wall base into a jagged reflection.
 */
export function reflectionMap(mask: Uint8Array, width: number, height: number, minGap = 0.01): Float32Array {
  const out = new Float32Array(width * height).fill(-1);
  const gap = Math.max(2, Math.round(height * minGap));
  for (let x = 0; x < width; x++) {
    let contact = -1;
    let run = 0;
    let seenObject = false;
    for (let y = 0; y < height; y++) {
      const i = y * width + x;
      if (mask[i] > 127) {
        if (seenObject && (run >= gap || contact < 0)) contact = y;
        run = 0;
        if (contact >= 0) out[i] = contact / height;
      } else {
        run++;
        seenObject = true;
      }
    }
  }
  return smoothRows(out, width, height, Math.max(1, Math.round(width * 0.006)));
}

/** Per row, averages each contact with neighbors within `radius` px whose contact is close (same object). */
function smoothRows(map: Float32Array, width: number, height: number, radius: number): Float32Array {
  const out = new Float32Array(map.length);
  const close = 0.02;
  for (let y = 0; y < height; y++) {
    const o = y * width;
    for (let x = 0; x < width; x++) {
      const c = map[o + x];
      if (c < 0) {
        out[o + x] = c;
        continue;
      }
      let sum = 0;
      let n = 0;
      for (let k = Math.max(0, x - radius); k <= Math.min(width - 1, x + radius); k++) {
        const v = map[o + k];
        if (v >= 0 && Math.abs(v - c) < close) {
          sum += v;
          n++;
        }
      }
      out[o + x] = sum / n;
    }
  }
  return out;
}
